import React, { FC, useState } from 'react'
import { Title, BrowserLocaleDateFormatter, ActivityDates, AssignmentData, AssignmentInput, ApplicationDocuments, AssignmentActivity, FLOW_STATUS_NAMES, NewButton, Collapse, NewModal, DrawerModal, Divider, ConfirmAction, FlowAssignment, FLOW_STATUS, Tooltip, ROLES, ROLE_NAME_INITIALS, ADMIN_PORTAL_ROLES, SvgIcon, User, NotificationType, getFlowElement } from 'prace-common-components'
import Grid from '@material-ui/core/Grid'
import { $ReviewContainer, $ReviewEntry, $Param, $Divider, $ParamName, $AddAssignment, $ApplicationActions, $AssignmentContainer, $CollapseContainer, $TimelineContainer, $TimelineEntry, $EmailTitle, $ClickableUser, $ChangesSnapshot, $ChangesSnapshotToggle, $ChangesSnapshotList, $ChangesSnapshotItem, $ChangesSnapshotEmpty } from './styles'
import { DeadlineModal } from 'components/DeadlineModal'
import { ReviewModal } from 'components/ReviewModal'
import { RequestChanges } from 'components/RequestChanges'
import { AssignmentVersions, formatPreviousValues } from './AssignmentVersions'
import { ApplicationActionsProps } from './types'
import { ChangeUserVisibilityModal } from 'components/ChangeUserVisibilityModal'
import { useGetImpersonationActionsQuery, useSearchUsersMutation, useUpdateUserMutation } from 'store/api/users'
import { useGetCallDocumentsQuery } from 'store/api/calls'
import { UpdateUserDetailsPayload } from 'store/api/users/types'
import { useTheme } from 'styled-components'
import { UserDetails } from 'pages/Users/UserDetails'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { CAN_VIEW_IMPERSONATION } from 'components/ImpersonationActivity'
import { adminName, getActionChanges, isAdminEdit, parseAssignmentId } from 'components/ImpersonationActivity/format'

type ParsedChangedOwner = {
  previousOwner?: { id: number; email: string },
  newOwner?: { id: number; email: string },
}

const parseChangedOwnerDesc = (description: string | string[]): ParsedChangedOwner | null => {
  try {
    const str = Array.isArray(description) ? '{' + description.join(',') + '}' : description
    return JSON.parse(str)
  } catch {
    return null
  }
}

type ChangesRequestedSnapshot = {
  editableElementIds?: number[],
  editableNewElementIds?: number[],
  previousData?: { id: number; input?: AssignmentInput }[],
}

/* The API's knex postProcessResponse splits any `{...}` varchar into a string[]
 * (treating it as a Postgres array literal). Our JSON snapshot starts/ends with
 * `{}`, so it arrives here as a string[] that needs to be re-joined. */
const parseChangesRequestedDesc = (description?: string | string[]): ChangesRequestedSnapshot | null => {
  if(!description) return null
  try {
    const str = Array.isArray(description) ? '{' + description.join(',') + '}' : description
    const parsed = JSON.parse(str)
    if(parsed && typeof parsed === 'object' && ('previousData' in parsed || 'editableNewElementIds' in parsed)) {
      return parsed as ChangesRequestedSnapshot
    }
    return null
  } catch {
    return null
  }
}

const buildChangedOwnerText = (
  activity: AssignmentActivity,
  timelineUsers: User[],
  setSelectedUserDrawer: (user: User) => void,
): { prevOwnerLink: JSX.Element | null; newOwnerLink: JSX.Element | null } => {
  const desc = activity.description ? parseChangedOwnerDesc(activity.description) : null
  let prevOwnerLink: JSX.Element | null = null
  let newOwnerLink: JSX.Element | null = null

  if (desc) {
    const prevOwnerId = desc.previousOwner?.id
    const prevOwnerEmail = desc.previousOwner?.email || 'unknown'
    const prevOwner = timelineUsers.find((user) => user.id === prevOwnerId)
    const prevOwnerName = prevOwner?.firstName || prevOwner?.lastName ? `${prevOwner.firstName} ${prevOwner.lastName}` : prevOwnerEmail
    const isAdminPrev = prevOwner && ADMIN_PORTAL_ROLES.includes(prevOwner.role as ROLES)
    if (prevOwnerId && prevOwner) {
      prevOwnerLink = <$ClickableUser onClick={() => setSelectedUserDrawer(prevOwner)}>{prevOwnerName}{isAdminPrev ? ' (PRO)' : ''}</$ClickableUser>
    }
    const newOwnerId = desc.newOwner?.id
    const newOwnerEmail = desc.newOwner?.email || 'unknown'
    const newOwner = timelineUsers.find((user) => user.id === newOwnerId)
    const newOwnerName = newOwner?.firstName || newOwner?.lastName ? `${newOwner.firstName} ${newOwner.lastName}` : newOwnerEmail
    const isAdminNew = newOwner && ADMIN_PORTAL_ROLES.includes(newOwner.role as ROLES)
    if (newOwnerId && newOwner) {
      newOwnerLink = <$ClickableUser onClick={() => setSelectedUserDrawer(newOwner)}>{newOwnerName}{isAdminNew ? ' (PRO)' : ''}</$ClickableUser>
    }
  }

  return { prevOwnerLink, newOwnerLink }
}

export const ApplicationActions: FC<ApplicationActionsProps> = (
  {
    handleSelect,
    handleReview,
    handlePartitionChange,
    assignments,
    handleDownloadClick,
    proposalId = '',
    applicationId,
    documents,
    handleDeadline,
    handleOpenAssign,
    handleOpenInvite,
    handleRevoke,
    setLeadAssignment,
    handleOpenChangeApplicant,
    selectedAssignment,
    step,
    call,
    hasLead,
    assignmentPartition,
    handleRequestChanges,
    downloadConsolidated,
    downloadAll,
    onChangeUserVisibility,
  },
) => {
  const theme = useTheme() as { colors: { primary: string; secondary: string; text: { disabled: string } }}
  const dispatch = useAppDispatch()
  const [timelineOpen, setTimelineOpen] = useState<boolean>(false)
  const [deadlineOpen, setDeadlineOpen] = useState<FlowAssignment | undefined>(undefined)
  const [requestChangesOpen, setRequestChangesOpen] = useState<FlowAssignment | undefined>(undefined)
  const [reviewOpen, setReviewOpen] = useState<number | undefined>(undefined)
  const [documentsOpen, setDocumentsOpen] = useState<boolean>(false)
  /* Documents attached to the call, listed alongside the application's own documents */
  const { data: callDocuments = [] } = useGetCallDocumentsQuery(call.id, { skip: !call.id || !documentsOpen })
  const [changeVisibilityOpen, setChangeVisibilityOpen] = useState<boolean>(false)
  const [timelineUsers, setTimelineUsers] = React.useState<User[]>([])
  const [selectedUserDrawer, setSelectedUserDrawer] = useState<User | null>(null)
  const [expandedSnapshots, setExpandedSnapshots] = useState<Record<number, boolean>>({})
  const [expandedImpersonation, setExpandedImpersonation] = useState<Record<number, boolean>>({})
  const [searchUsers] = useSearchUsersMutation()
  const [updateUser] = useUpdateUserMutation()
  const currentRole = useAppSelector((state) => state.auth.user?.role)
  const currentUserId = useAppSelector((state) => state.auth.user?.id)
  /* Until the user accepts their own assignment, Assign Another User is the only way to bring someone in */
  const pendingAcceptance = assignments.some(
    (a) => a.ownerId == currentUserId && a.status === FLOW_STATUS.ASSIGNED)
  /* Admin-scoped endpoint: a PRO reviewer would get a 403, which the admin portal turns into a logout. */
  const { data: impersonationActions = [] } = useGetImpersonationActionsQuery(
    { applicationId },
    {
      skip: !applicationId || !CAN_VIEW_IMPERSONATION.includes(currentRole as ROLES),
      refetchOnMountOrArgChange: true,
    })

  const resolveElementTitle = (elementId: number) => getFlowElement(call, elementId)?.element?.title

  /* FIXME: THIS ACTIVITY INFO SHOULD COME FROM BE IN CASE OF ADMIN REQUESTING */
  /* TODO: This request can be done fewer times, one time for each step */
  /* Instead of doing it everytime the user changes step */
  React.useEffect(() => {
    /* 
        let search: string[] = []
    assignments.forEach((assignment) => {
      const assignmentUserIds: string[] = assignment.activities.map((activity) => String(activity.authorId))
      search = [...search, ...assignmentUserIds]
    })
    if(search.length) dispatch(USERS_SUGGESTIONS_EVENTS.LOAD_USER_SUGGESTIONS, { search, page: 1 })
    
    */
    const getActivityUsers = async () => {
      let userIds: number[] = []
      assignments.forEach((assignment) => {
        const assignmentUserIds = assignment.activities.map((activity) => {
          const ids = [activity.authorId]
          // Also extract previous and new owner IDs from changed_owner activities
          if (activity.status === FLOW_STATUS.CHANGED_OWNER && activity.description) {
            const desc = parseChangedOwnerDesc(activity.description)
            if (desc?.previousOwner?.id) {
              ids.push(desc.previousOwner.id)
            }
            if (desc?.newOwner?.id) {
              ids.push(desc.newOwner.id)
            }
          }
          return ids
        }).flat()
        userIds = [...userIds, ...assignmentUserIds]
      })
      /* Remove -1 since this means that the assignments were created by automations */
      const filteredUserIds = userIds.filter((userId) => userId !== -1)
      const uniqueUserIds = [...new Set(filteredUserIds)]
      if(uniqueUserIds.length) {
        const data = await searchUsers({
          search: [''],
          userIds: uniqueUserIds,
          applicationId,
          page: 0,
          pageSize: 30,
          showRoles: true,
        }).unwrap()
        setTimelineUsers(data.users || [])
      }
    }
    getActivityUsers().catch((err) => console.log(err))
  }, [assignments, searchUsers, applicationId])

  const handleSelectAssignment = (assignmentIdx: number) => {
    handleSelect(assignmentIdx, 'assignment')
  }

  const handleDeadlineSubmit = (deadlineOpenOd: number, deadline: Nullable<string>) => {
    handleDeadline(deadlineOpenOd, deadline)
    setDeadlineOpen(undefined)
  }

  const handleChangesRequest = (assignment: FlowAssignment, elementIds: number[],deadline?: string, reason?:string) => {
    const { data = [], id = 0 } = assignment
    let editableElementIds: number[] = []
    const editableNewElementIds: number[] = []
    elementIds.forEach((elementId) => {
      const foundDatas = data.filter(
        (assignmentData: AssignmentData) => elementId == Number(assignmentData.flowElementId) ||
          elementId == Number(assignmentData.dataGroupId) ||
          elementId == Number(assignmentData.dataGroup?.flowElementId || 0))
        .map((assignmentData: AssignmentData) => assignmentData.id as number)
      if(foundDatas.length) editableElementIds = editableElementIds.concat(foundDatas)
      else editableNewElementIds.push(elementId)
    })

    handleRequestChanges(id, editableElementIds, editableNewElementIds, deadline, reason)
    setRequestChangesOpen(undefined)
  }

  const handleAssignmentReview = (assignmentId: number, review: string, deadline?: string, reason?: string) => {
    handleReview(assignmentId, review, deadline, reason)
    setReviewOpen(undefined)
  }

  const handleChangeVisibility = async (ids: number[]) => {
    await onChangeUserVisibility(ids)
    setChangeVisibilityOpen(false)
  }

  const changeUserDetails = async (payload: UpdateUserDetailsPayload) => {
    try {
      await updateUser(payload).unwrap()
      setTimelineUsers((prev) => prev.map((u) => u.id === payload.id ? { ...u, ...payload } : u))
      dispatch({
        type: 'notification',
        payload: {
          type: NotificationType.success,
          msg: 'User details have been updated successfully',
        },
      })
      setSelectedUserDrawer(null)
    } catch (err) {
      console.log(err)
      dispatch({
        type: 'notification',
        payload: {
          type: NotificationType.error,
          msg: 'Failed to update user details',
        },
      })
    }
  }

  const assignmentLength = assignments.length
  const isFirstStep = !step

  return (
    <$ApplicationActions container spacing={2}>
      {assignmentLength ?
        <Grid item xs={12}>
          {isFirstStep ? null : <Title alternate noMargin>Assignments</Title>}
          {assignments.map((assignment, assignmentIdx: number) => {
            const activityReasons = assignment.activities?.filter(
              (act) => (!!act?.reason && act?.status !== FLOW_STATUS.ACCEPTED)) || []
            /* A revoked user is no longer on the assignment, so it is greyed out */
            const isRevoked = assignment.status === FLOW_STATUS.REVOKED
            const dotColor = isRevoked ? theme.colors.text.disabled : theme.colors.secondary
            return <$AssignmentContainer container key={assignmentIdx}>
              <Tooltip placement='top' arrow show={selectedAssignment != assignmentIdx} text={assignment.owner?.email}>
                <div>
                  <Title
                    subTitle
                    onClick={() => handleSelectAssignment(assignmentIdx)}
                    noMargin
                    icon='circle'
                    hasBack
                    multiline
                    bold={selectedAssignment == assignmentIdx}
                    iconProps={{color: dotColor, size: 10, clickable: false}}
                    color={isRevoked ? theme.colors.text.disabled
                      : selectedAssignment == assignmentIdx ? theme.colors.secondary : undefined}>
                    {hasLead && assignment.isLead && 'Lead - '}
                    {assignment.owner?.firstName ?
                      `${assignment.owner?.firstName || ''} ${assignment.owner?.lastName || ''} (${assignment.owner?.role ? ROLE_NAME_INITIALS[assignment.owner.role as ROLES] : ''})`
                      : `#${assignmentIdx + 1} ${assignment.owner?.role ? ROLE_NAME_INITIALS[assignment.owner.role as ROLES] : ''}`}
                    {selectedAssignment == assignmentIdx && <$EmailTitle> {assignment.owner?.email} </$EmailTitle>}
                    <div>{FLOW_STATUS_NAMES[assignment.status]}</div>
                  </Title>
                </div>
              </Tooltip>
              {selectedAssignment == assignmentIdx &&
              <>
                <$Param>ID: <$ParamName>{assignment?.id || ''}</$ParamName></$Param>
                <$Param>Partition: 
                  <$ParamName>{assignmentPartition?.name || ''}</$ParamName>
                  <SvgIcon onClick={() => handlePartitionChange(assignment.id)} clickable name='altEdit' size='16' color='#8D9192' />
                </$Param>
                <$CollapseContainer>
                  <Collapse
                    title='Timeline'
                    open={timelineOpen}
                    handleOpen={setTimelineOpen}
                    icon='circle'
                    iconProps={{color: dotColor, size: 10, clickable: false}}
                  >
                    <$TimelineContainer container direction='column' spacing={1}>
                      {assignment.activities.map((activity: AssignmentActivity) => {
                        const author = timelineUsers.find((user) => user.id === activity.authorId)
                        const adminAssigned = ADMIN_PORTAL_ROLES.includes(author?.role as ROLES)
                        const isChangedOwner = activity.status === FLOW_STATUS.CHANGED_OWNER
                        const isChangesRequested = activity.status === FLOW_STATUS.CHANGES_REQUESTED
                        const changesSnapshot = isChangesRequested
                          ? parseChangesRequestedDesc(activity.description)
                          : null
                        const hasSnapshotContent = !!changesSnapshot && (
                          (changesSnapshot.previousData?.length || 0) > 0 ||
                          (changesSnapshot.editableNewElementIds?.length || 0) > 0
                        )
                        const snapshotExpanded = !!expandedSnapshots[activity.id]
                        const activityText = activity.description?.includes('lead') && !changesSnapshot
                          ? activity.description
                          : FLOW_STATUS_NAMES[activity.status as keyof typeof FLOW_STATUS_NAMES] || activity.status
                        const { prevOwnerLink, newOwnerLink } = isChangedOwner && activity.description
                          ? buildChangedOwnerText(activity, timelineUsers, setSelectedUserDrawer)
                          : { prevOwnerLink: null, newOwnerLink: null }
                        return <Grid item xs key={activity.id}>
                          <$TimelineEntry>
                            {`${BrowserLocaleDateFormatter.format(new Date(activity.createdAt))} - ${isChangedOwner ? FLOW_STATUS_NAMES.changed_owner : activityText}`}
                            {isChangedOwner && (
                              <> from {prevOwnerLink || 'unknown'} to {newOwnerLink || 'unknown'}, by </>
                            )}
                            {!isChangedOwner && ' by '}
                            {author ?
                              <$ClickableUser onClick={() => setSelectedUserDrawer(author)}>{`${author?.firstName || ''} ${author?.lastName || ''}${adminAssigned ? ' (PRO)' : ''}${!author?.firstName && !author?.lastName ? author?.email : ''}`}</$ClickableUser>
                              : 'an action'}
                          </$TimelineEntry>
                          {hasSnapshotContent &&
                            <$ChangesSnapshot>
                              <$ChangesSnapshotToggle
                                type='button'
                                onClick={() => setExpandedSnapshots((prev) =>
                                  ({ ...prev, [activity.id]: !prev[activity.id] }))}
                              >
                                {snapshotExpanded ? 'Hide previous values' : 'Show previous values'}
                              </$ChangesSnapshotToggle>
                              {snapshotExpanded && <>
                                {!!changesSnapshot?.previousData?.length &&
                                  <$ChangesSnapshotList>
                                    {changesSnapshot.previousData.map((entry) => {
                                      const value = formatPreviousValues(entry.input)
                                      return <$ChangesSnapshotItem key={entry.id}>
                                        <b>{entry.input?.title || `#${entry.id}`}: </b>
                                        {value || <$ChangesSnapshotEmpty>(empty)</$ChangesSnapshotEmpty>}
                                      </$ChangesSnapshotItem>
                                    })}
                                  </$ChangesSnapshotList>
                                }
                                {!!changesSnapshot?.editableNewElementIds?.length && <>
                                  <div><b>New fields requested:</b></div>
                                  <$ChangesSnapshotList>
                                    {changesSnapshot.editableNewElementIds.map((elementId) => {
                                      const { element } = getFlowElement(call, elementId)
                                      return <$ChangesSnapshotItem key={elementId}>
                                        {element?.title || `#${elementId}`}
                                      </$ChangesSnapshotItem>
                                    })}
                                  </$ChangesSnapshotList>
                                </>}
                              </>}
                            </$ChangesSnapshot>
                          }
                        </Grid>
                      })}
                      {impersonationActions
                        .filter((imp) => parseAssignmentId(imp.path) === assignment.id)
                        .map((imp) => {
                          const impChanges = getActionChanges(imp, resolveElementTitle)
                          /* A direct admin edit is a single field with its values, so it is always shown open */
                          const adminEdit = isAdminEdit(imp)
                          const impExpanded = adminEdit || !!expandedImpersonation[imp.id]
                          return <Grid item xs key={`imp-${imp.id}`}>
                            <$TimelineEntry>
                              {`${BrowserLocaleDateFormatter.format(new Date(imp.createdAt))} - `}
                              {adminEdit ? 'Admin edit' : 'Changes'}{' by '}
                              {adminName(imp)} (Admin)
                            </$TimelineEntry>
                            {!!impChanges.length &&
                              <$ChangesSnapshot>
                                {!adminEdit && <$ChangesSnapshotToggle
                                  type='button'
                                  onClick={() => setExpandedImpersonation((prev) =>
                                    ({ ...prev, [imp.id]: !prev[imp.id] }))}
                                >
                                  {impExpanded ? 'Hide changes' : 'Show changes'}
                                </$ChangesSnapshotToggle>}
                                {impExpanded &&
                                  <$ChangesSnapshotList>
                                    {impChanges.map((change, idx) =>
                                      <$ChangesSnapshotItem key={idx}>
                                        <b>{change.label}: </b>
                                        {change.value || <$ChangesSnapshotEmpty>(empty)</$ChangesSnapshotEmpty>}
                                      </$ChangesSnapshotItem>,
                                    )}
                                  </$ChangesSnapshotList>
                                }
                              </$ChangesSnapshot>
                            }
                          </Grid>
                        })}
                    </$TimelineContainer>
                  </Collapse>
                </$CollapseContainer>
                <AssignmentVersions
                  assignment={assignment} timelineUsers={timelineUsers} applicationId={applicationId} />
                <Grid container direction='column' spacing={1}>
                  {!!activityReasons.length && <>
                    <$Divider />
                    <Title subTitle noMargin>Review Comments</Title>
                    <$ReviewContainer>
                      {activityReasons.map((activity) => <Grid item xs key={activity.id}>
                        <$ReviewEntry>
                          <b>{`${BrowserLocaleDateFormatter.format(new Date(activity.createdAt))} ${FLOW_STATUS_NAMES[activity.status]}: `}</b>{activity?.reason || ''}
                        </$ReviewEntry>
                      </Grid>)}
                    </$ReviewContainer>
                  </>}
                  {isFirstStep && <Grid item xs>
                    <NewButton fullWidth variant='outlined' onClick={handleOpenChangeApplicant}>Change Applicant</NewButton>
                  </Grid>}
                  <Grid item xs>
                    <NewButton fullWidth variant='outlined' onClick={() => setDeadlineOpen(assignment)}>Change Deadline</NewButton>
                  </Grid>
                  <Grid item xs>
                    <NewButton fullWidth variant='outlined' onClick={() => setRequestChangesOpen(assignment)}>Request Changes</NewButton>
                  </Grid>
                  <Grid item xs>
                    <NewButton fullWidth variant='outlined' onClick={() => setReviewOpen(assignment.id)}>Review</NewButton>
                  </Grid>
                  {assignment.status !== FLOW_STATUS.REFUSED && <Grid item xs>
                    <ConfirmAction
                      title={assignment.status === FLOW_STATUS.REVOKED ? 'Unrevoke assignment' : 'Revoke assignment'}
                      question={assignment.isLead ? 'You are revoking the lead assignment. There will be no lead assignment afther this action.' : undefined}
                      description={`This action will ${assignment.status === FLOW_STATUS.REVOKED ? 'add' : 'remove'} the user from the assignment.`}
                      onConfirm={() => handleRevoke(assignment.id)}
                    >
                      <NewButton fullWidth error variant='outlined'>{assignment.status === FLOW_STATUS.REVOKED ? 'Unrevoke' : 'Revoke'}</NewButton>
                    </ConfirmAction>
                  </Grid>}
                  {hasLead && !assignment.isLead &&
                    <Grid item xs>
                      <NewButton fullWidth variant='outlined' onClick={() => setLeadAssignment(assignment)}>Make Lead Assignment</NewButton>
                    </Grid>
                  }
                  <Grid item xs>
                    <NewButton
                      fullWidth
                      variant='outlined'
                      onClick={() => setChangeVisibilityOpen(true)}
                    >
                      Change Visibility
                    </NewButton>
                  </Grid>
                  {changeVisibilityOpen &&
                    <NewModal
                      noTopMargin
                      open={changeVisibilityOpen}
                      title={'Edit View Access'}
                      onClose={() => setChangeVisibilityOpen(false)}
                    >
                      <ChangeUserVisibilityModal
                        callTitle={call.title}
                        permissions={call.flow?.length ? call.flow[step]?.permissions : undefined}
                        assignment={assignment as FlowAssignment}
                        handleSave={(ids) => handleChangeVisibility(ids)}
                        handleCancel={() => setChangeVisibilityOpen(false)}
                      />
                    </NewModal>
                  }
                  {(assignment.submittedAt || assignment.deadline) && <Grid item xs>
                    <ActivityDates
                      deadline={
                        (assignment.submittedAt && (assignment.status !== FLOW_STATUS.CHANGES_REQUESTED)) 
                        || !assignment.deadline ? undefined : new Date(assignment.deadline)}
                      submit={assignment.submittedAt ? new Date(assignment.submittedAt) : undefined}
                    />
                  </Grid>}
                </Grid>
              </>
              }
            </$AssignmentContainer>
          })}
          <Divider />
        </Grid>
        : null }
      <$AddAssignment>
        {!isFirstStep && !pendingAcceptance &&
          <>
            <NewButton creator variant='text' onClick={handleOpenAssign}>
            Add Assignment
            </NewButton>
            <NewButton creator variant='text' onClick={handleOpenInvite}>
            Invite User
            </NewButton>
          </>
        }
      </$AddAssignment>
      <Grid item xs={12}>
        <Title subTitle onClick={() => setDocumentsOpen(true)} color={theme.colors.primary} hasBack iconProps={{color: theme.colors.primary, clickable: false}} noMargin icon='documents'>Documents</Title>
      </Grid>
      {deadlineOpen &&
        <NewModal open alternateTitle title='Change Deadline' onClose={() => setDeadlineOpen(undefined)}>
          <DeadlineModal
            currentDeadline={deadlineOpen.deadline}
            handleDeadline={(deadline: Nullable<string>) => handleDeadlineSubmit(deadlineOpen.id, deadline)}
            handleCancel={() => setDeadlineOpen(undefined)}
          />
        </NewModal>
      }

      {/* Only appears if tthe assignment has been submitted? */}
      {requestChangesOpen &&
        <NewModal open alternateTitle title='Request Changes' onClose={() => setRequestChangesOpen(undefined)}>
          <RequestChanges
            call={call}
            selectedStep={step}
            handleRequestChanges={(elementIds: number[], deadline?: string, reason?: string) =>
              handleChangesRequest(requestChangesOpen, elementIds, deadline, reason)}
            handleCancel={() => setRequestChangesOpen(undefined)}
          />
        </NewModal>
      }

      {documentsOpen && <DrawerModal open title={proposalId} handleClose={() => setDocumentsOpen(false)}>
        <ApplicationDocuments isPRO
          call={call}
          uid={proposalId}
          handleDownloadClick={handleDownloadClick}
          documents={documents}
          callDocuments={callDocuments}
          downloadConsolidated={() => downloadConsolidated(proposalId)}
          downloadAll={() => downloadAll(proposalId)}
        />
      </DrawerModal>}
      {reviewOpen &&
        <NewModal open alternateTitle title='Review Assignment' onClose={() => setReviewOpen(undefined)}>
          <ReviewModal
            handleReview={(review: string, deadline?: string, reason?: string) =>
              handleAssignmentReview(reviewOpen, review, deadline, reason)}
            handleCancel={() => setReviewOpen(undefined)}
          />
        </NewModal>}
      <UserDetails
        user={selectedUserDrawer}
        open={!!selectedUserDrawer}
        handleClose={() => setSelectedUserDrawer(null)}
        onChangeUserDetails={changeUserDetails}
      />
    </$ApplicationActions>
  )
}
